// The image URL the PDF generator must embed for a photo.
//
// CRITICAL print-quality invariant: the printed album must always embed the
// full-resolution ORIGINAL — never the small ~512px editor thumbnail. The
// worker overloads `signed_url` as the editor DISPLAY tier
// (thumbnail ?? preview ?? original) for fast on-screen loading, and exposes
// the untouched original separately as `original_signed_url`
// (see worker/src/routes/photos.ts — "kept EXACTLY as-is so the PDF generator
// always reaches the print-quality file"). The CLIENT-side PDF generators must
// therefore source from `original_signed_url`, falling back to `signed_url`
// only when no original was signed (older API shape / cover images).
//
// Regression guard: do NOT reorder this to read `signed_url` first — that would
// silently print 512px thumbnails on a per-page-priced printed product.
export function pdfPhotoSourceUrl(photo: {
  original_signed_url?: string | null;
  signed_url?: string | null;
}): string | null {
  return photo.original_signed_url ?? photo.signed_url ?? null;
}

/**
 * The image the QUICK 3D preview draws for a photo (lib/quickPreview.ts) —
 * never anything that prints.
 *
 * The ~1600 px preview copy when it is a plain Supabase object (the generator
 * fetch()es it; a Cloudflare Images URL may refuse cross-origin reads), else
 * the original. Never the editor's display transform: Supabase bills image
 * transforms per origin image, and the preview copy is already that size.
 * The preview copy is the same picture, upright, same aspect — a repointed
 * (rotated) photo has its stale tiers dropped by migration 169, so it falls
 * back to the original here.
 */
export function quickPhotoUrl(photo: {
  preview_signed_url?: string | null;
  original_signed_url?: string | null;
  signed_url?: string | null;
}): string | null {
  const preview = photo.preview_signed_url ?? null;
  if (preview && /\/storage\/v1\/object\/sign\//.test(preview)) return preview;
  return pdfPhotoSourceUrl(photo);
}
